import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Box, IconButton, Tooltip, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";

import { contactApi } from "@api/index";
import { RowHeaderTextCenter } from "@components/shared/styles";
import { useUserReducer } from "@store/user";
import { getTranslateByKey } from '@/main/services/utils';
import * as S from "./styles";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { ISupportContact } from "@/main/models/response/supportContactResponse";
import KOGBoxGrid from "../../shared/kogBoxGrid/kogBoxGrid";
import { KOGTableRow } from "../../shared/boxGrid/styles";
import { KOGTableCell } from "../../shared/kogBoxGrid/styles";

const SupportContactsGrid: React.FC = () => {
	const theme = useTheme();
	const [contacts, setContacts] = useState<ISupportContact[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isFetchingMore, setIsFetchingMore] = useState(false);

	const { currentUser } = useUserReducer();
	const guidCompany = currentUser?.guidCompany;

	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);

	const ID_SCROLL_TARGET = "support-contacts-grid-scroll";

	const rowHeaders = [
		<RowHeaderTextCenter>
			<span>{getTranslateByKey("information.name")}</span>
		</RowHeaderTextCenter>,
		<RowHeaderTextCenter>
			<span>{getTranslateByKey("information.email")}</span>
		</RowHeaderTextCenter>,
		<RowHeaderTextCenter>
			<span>{getTranslateByKey("information.phone")}</span>
		</RowHeaderTextCenter>,
		<RowHeaderTextCenter>
			<span>{getTranslateByKey("information.cellphoneNr")}</span>
		</RowHeaderTextCenter>,
		<RowHeaderTextCenter>
			<span>{getTranslateByKey("information.businessHour")}</span>
		</RowHeaderTextCenter>,
	];

	const refreshContacts = useCallback(async () => {
		setIsLoading(true);
		setPage(1);
		setHasMore(true);
		try {
			const response = await contactApi.getContacts({ guidCompany, pageIndex: 1, pageSize: PaginationEnum.PageSize10 });
			const responseData = response.data;

			if (responseData.results) {
				setContacts(responseData.results || []);
				setHasMore(responseData.pageNumber < responseData.totalNumberOfPages);
			}
		} finally {
			setIsLoading(false);
		}
	}, [guidCompany, setIsLoading, setPage, setHasMore, setContacts]);

	const fetchMoreContacts = useCallback(async () => {
		if (!hasMore || isFetchingMore) return;

		setIsFetchingMore(true);
		const nextPage = page + 1;

		try {
			const response = await contactApi.getContacts({
				guidCompany,
				pageIndex: nextPage,
				pageSize: PaginationEnum.PageSize10,
			});
			const responseData = response.data;

			if (responseData && responseData.results) {
				setContacts((prev) => [...prev, ...responseData.results]);
				setPage(nextPage);
				setHasMore(responseData.pageNumber < responseData.totalNumberOfPages);
			} else {
				setHasMore(false);
			}
		} catch {
			setHasMore(false);
		} finally {
			setIsFetchingMore(false);
		}
	}, [page, hasMore, isFetchingMore]);

	useEffect(() => {
		refreshContacts();
	}, [refreshContacts]);

	const handleWhatsappClick = (phone: string) => {
		const cleanPhone = phone.replace(/\D/g, "");
		const message = getTranslateByKey("information.technicalSupportRequest");

		const url = `${import.meta.env.VITE_WHATSAPP_BASE_URL}${cleanPhone}?text=${encodeURIComponent(message)}`;
		window.open(url, "_blank");
	};

	const handleEmailClick = (email: string) => {
		const messageBody = getTranslateByKey("information.technicalSupportRequest");
		const mailtoUrl = `mailto:${email}?body=${encodeURIComponent(messageBody)}`;
		window.location.href = mailtoUrl;
	};

	const handlePhoneClick = (phone: string) => {
		const cleanPhone = phone.replace(/\D/g, "");
		window.open(`tel:${cleanPhone}`, "_blank");
	};

	const renderCellContentText = (text: string) => {
		return <S.CellContent style={{ textAlign: "center", width: "100%" }}>{text ? text : "--"}</S.CellContent>;
	};

	const renderCellContentTextPhone = (text: string) => {
		return (
			<S.CellContent style={{ textAlign: "center", width: "100%" }}>
				{!!text && text.length > 4 ? text : "--"}
			</S.CellContent>
		);
	};

	return (
		<KOGBoxGrid
			rowHeaders={rowHeaders}
			sizeCell="small"
			typeGrid="default"
			marginCustom="0"
			showEndMessage={false}
			infiniteScrollConfig={{
				dataLength: contacts.length,
				fetchData: fetchMoreContacts,
				hasMore: hasMore,
				isLoading: isLoading || isFetchingMore,
				idScrollTarget: ID_SCROLL_TARGET,
			}}
		>
			{contacts.length === 0 && !isLoading ? (
				<KOGTableRow>
					<KOGTableCell colSpan={5}>
						<S.CellContent style={{ textAlign: "center" }}>
							{getTranslateByKey("information.noResultFound")}
						</S.CellContent>
					</KOGTableCell>
				</KOGTableRow>
			) : (
				contacts.map((contact, index) => (
					<KOGTableRow key={contact.guid}>
						<KOGTableCell>
							<Box sx={{ minWidth: "100px", overflow: "hidden", textOverflow: "ellipsis" }}>
								<Tooltip title={renderCellContentText(`${contact.firstName} ${contact.lastName}`)}>
									{renderCellContentText(`${contact.firstName} ${contact.lastName}`)}
								</Tooltip>
							</Box>
						</KOGTableCell>

						<KOGTableCell>
							<Box
								display="flex"
								alignItems="center"
								gap={1}
								sx={{ mr: 1, maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis" }}
							>
								<Tooltip title={renderCellContentText(contact.email)}>
									<>
										{renderCellContentText(contact.email)}
										{contact.email && (
											<IconButton
												id={`id-support-contacts-btn-email.${index}`}
												size="small"
												onClick={() => handleEmailClick(contact.email)}
											>
												<EmailIcon fontSize="small" sx={{ fontSize: 18, color: theme.palette.common.black }} />
											</IconButton>
										)}
									</>
								</Tooltip>
							</Box>
						</KOGTableCell>

						<KOGTableCell>
							<Box
								display="flex"
								alignItems="center"
								gap={1}
								sx={{ minWidth: "140px", overflow: "hidden", textOverflow: "ellipsis" }}
							>
								<Tooltip title={renderCellContentTextPhone(contact.phone)}>
									<>
										{renderCellContentTextPhone(contact.phone)}
										{contact.phoneIsWhatsApp && contact.phone.length > 4 && (
											<IconButton
												id={`id-support-contacts-btn-whatsapp.${index}`}
												size="small"
												onClick={() => handleWhatsappClick(contact.phone)}
											>
												<WhatsAppIcon fontSize="small" sx={{ fontSize: 18, color: theme.palette.common.black }} />
											</IconButton>
										)}
										{contact.phone && contact.phone.length > 4 && (
											<IconButton
												id={`id-support-contacts-btn-phone.${index}`}
												size="small"
												onClick={() => handlePhoneClick(contact.phone)}
											>
												<PhoneIcon fontSize="small" sx={{ fontSize: 18, color: theme.palette.common.black }} />
											</IconButton>
										)}
									</>
								</Tooltip>
							</Box>
						</KOGTableCell>

						<KOGTableCell>
							<Box
								display="flex"
								alignItems="center"
								gap={1}
								sx={{ minWidth: "140px", overflow: "hidden", textOverflow: "ellipsis" }}
							>
								<Tooltip title={renderCellContentTextPhone(contact.cellphone)}>
									<>
										{renderCellContentTextPhone(contact.cellphone)}
										{contact.cellphoneIsWhatsApp && contact.cellphone.length > 4 && (
											<IconButton
												id={`id-support-contacts-btn-cellWhatsapp.${index}`}
												size="small"
												onClick={() => handleWhatsappClick(contact.cellphone)}
											>
												<WhatsAppIcon fontSize="small" sx={{ fontSize: 18, color: theme.palette.common.black }} />
											</IconButton>
										)}
										{!!contact.cellphone && contact.cellphone.length > 4 && (
											<IconButton
												id={`id-support-contacts-btn-cellPhone.${index}`}
												size="small"
												onClick={() => handlePhoneClick(contact.cellphone)}
											>
												<PhoneIcon fontSize="small" sx={{ fontSize: 18, color: theme.palette.common.black }} />
											</IconButton>
										)}
									</>
								</Tooltip>
							</Box>
						</KOGTableCell>

						<KOGTableCell>
							<Box sx={{ minWidth: "90px", overflow: "hidden", textOverflow: "ellipsis" }}>
								<Tooltip title={renderCellContentText(contact.businessHours)}>
									{renderCellContentText(contact.businessHours)}
								</Tooltip>
							</Box>
						</KOGTableCell>
					</KOGTableRow>
				))
			)}
		</KOGBoxGrid>
	);
};

export default SupportContactsGrid;
